import { useEffect, useMemo, useState } from 'react';
import { Alert, Box } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomAutoComplete,
  CustomTextField,
} from '../../../common/components';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import {
  INITIAL_WAREHOUSE_STATE,
  VALIDATION_RULES,
  WAREHOUSE_FORM_TEXTS as TEXTS,
  WAREHOUSE_STATUS_OPTIONS,
} from '../constants/warehousesConstants';
import { getWarehouseApiErrorMessage, validateWarehouse } from '../utils/warehousesUtils';
import { guardDialogClose } from '../../utils/actionLockUtils';

const WarehouseDialog = ({
  open,
  onClose,
  editMode,
  selectedWarehouse,
  branches = [],
  onCreate,
  onUpdate,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState(INITIAL_WAREHOUSE_STATE);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  const branchOptions = useMemo(
    () => [
      { value: '', label: TEXTS.noBranch },
      ...branches.map((branch) => ({ value: branch.uuid, label: branch.description })),
    ],
    [branches]
  );

  useEffect(() => {
    if (open) {
      if (editMode && selectedWarehouse) {
        setFormData({
          uuid: selectedWarehouse.uuid,
          code: selectedWarehouse.code || '',
          name: selectedWarehouse.name || '',
          uuid_branch: selectedWarehouse.uuid_branch || '',
          address: selectedWarehouse.address || '',
          is_active: selectedWarehouse.is_active ?? true,
        });
      } else {
        setFormData(INITIAL_WAREHOUSE_STATE);
      }
      setErrors({});
      setErrorMessage('');
    }
  }, [open, editMode, selectedWarehouse]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleStatusChange = (event) => {
    const value = event.target.value === 'true' || event.target.value === true;
    setFormData((prev) => ({
      ...prev,
      is_active: value,
    }));

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateWarehouse(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      code: formData.code.trim(),
      name: formData.name.trim(),
      uuid_branch: formData.uuid_branch || null,
      address: formData.address ? formData.address.trim() : null,
    };

    try {
      setErrorMessage('');

      if (editMode && formData.uuid) {
        await onUpdate(formData.uuid, {
          ...payload,
          is_active: formData.is_active,
        });
      } else {
        await onCreate(payload);
      }
      onClose();
    } catch (error) {
      console.error('Error al guardar el depósito:', error);
      setErrorMessage(getWarehouseApiErrorMessage(error));
    }
  };

  return (
    <DialogContainer open={open} onClose={guardDialogClose(isSubmitting, onClose)} size="small">
      <DialogTitle>{editMode ? TEXTS.editTitle : TEXTS.createTitle}</DialogTitle>

      {errorMessage && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CustomTextField
          label={TEXTS.code}
          name="code"
          value={formData.code}
          onChange={handleChange}
          required
          error={Boolean(errors.code)}
          inputProps={{ maxLength: VALIDATION_RULES.CODE_MAX_LENGTH }}
        />

        <CustomTextField
          label={TEXTS.name}
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          error={Boolean(errors.name)}
          inputProps={{ maxLength: VALIDATION_RULES.NAME_MAX_LENGTH }}
        />

        <CustomAutoComplete
          label={TEXTS.branch}
          name="uuid_branch"
          value={formData.uuid_branch}
          options={branchOptions}
          inputPlaceholder={TEXTS.branchPlaceholder}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onChange={handleChange}
        />

        <CustomTextField
          label={TEXTS.address}
          name="address"
          value={formData.address}
          onChange={handleChange}
          error={Boolean(errors.address)}
          inputProps={{ maxLength: VALIDATION_RULES.ADDRESS_MAX_LENGTH }}
        />

        {editMode && (
          <CustomAutoComplete
            label={TEXTS.status}
            name="is_active"
            value={formData.is_active}
            options={WAREHOUSE_STATUS_OPTIONS}
            onChange={handleStatusChange}
          />
        )}
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveText={editMode ? TEXTS.update : TEXTS.create}
        isSaving={isSubmitting}
      />
    </DialogContainer>
  );
};

export default WarehouseDialog;
